import type {
  DmFolder,
  DmFoldersSnapshot,
  DmPrivacy,
  FriendsSnapshot,
  FriendState,
  GuildFolder,
  GuildFoldersSnapshot,
  IceServer,
  MeResponse,
  MeUser,
  MuteLevel,
  OnboardingState,
  UserStatus,
} from '../../types/index.js';
import type { RequestFn } from '../http.js';
import type { GlobalSearchHit } from '../types.js';

/** Current-user surface: profile, mutes, push, blocks, friends, DM/server
 *  folders, sessions, global search, plus the two stateless system probes. */
export function makeMeApi(request: RequestFn) {
  return {
    me: () => request<MeResponse>('/api/me'),
    /** My channel notification rows — adopted by muteSettings on boot. A guild
     *  channel may hold an explicit ALL (it overrides a muted server). */
    listMyMutes: () =>
      request<{
        mutes: Array<{ channelId: string; level: MuteLevel; mutedUntil: string | null }>;
      }>('/api/me/mutes'),
    /** My server-wide notification rows (no row = ALL). */
    listMyGuildMutes: () =>
      request<{
        mutes: Array<{ guildId: string; level: Exclude<MuteLevel, 'ALL'>; mutedUntil: string | null }>;
      }>('/api/me/guild-mutes'),
    /**
     * Upload avatar BYTES (contract §2.2). The server forwards them to cispracs
     * and returns the canonical URL it stores them at.
     *
     * Not `updateMe({ avatarUrl })`: a URL the client supplies would have to be
     * fetched server-side, which is the SSRF the contract review rejected. The
     * web client has no `avatarUrl` path at all after this.
     */
    uploadAvatar: (bytes: Blob, filename = 'avatar') => {
      const form = new FormData();
      form.append('avatar', bytes, filename);
      return request<{ avatarUrl: string }>('/api/me/avatar', {
        method: 'POST',
        body: form,
      });
    },
    // No `displayName`: the nickname is owned by cispracs.ru and is not
    // settable here (contract `bunker-profile-alignment.md` §2.1). It reaches
    // Bunker through the login JWT and the `user-updated` webhook.
    // `avatarUrl` is `null`-only: clearing is still a PATCH, but the client can
    // no longer SUPPLY a URL — that is the whole point of §2.2 (no
    // client-controlled URL means no server-side fetch means no SSRF, and no
    // second store). Bytes go through `uploadAvatar` above.
    updateMe: (input: {
      avatarUrl?: null;
      status?: UserStatus;
      dmPrivacy?: DmPrivacy;
      showTeamInDms?: boolean;
  saveReadHistory?: boolean;
      onboardingState?: OnboardingState | null;
      bio?: string | null;
      statusText?: string | null;
    }) =>
      request<MeUser>('/api/me', {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),

    health: () => request<{ ok: boolean; ts: number }>('/health'),
    // `mediaBackend` is always 'sfu' (mesh voice was removed).
    rtcConfig: () =>
      request<{ mediaBackend: 'sfu'; iceServers: IceServer[] }>('/api/rtc/config'),

    // ---------- push notifications ----------
    request_pushConfig: () =>
      request<{ enabled: boolean; publicKey: string | null }>('/api/me/push/config'),
    subscribePush: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
      request<{ ok: true }>('/api/me/push/subscribe', {
        method: 'POST',
        body: JSON.stringify(sub),
      }),
    unsubscribePush: (endpoint: string) =>
      request<void>('/api/me/push/subscribe', {
        method: 'DELETE',
        body: JSON.stringify({ endpoint }),
      }),

    // ---------- blocks ----------
    listBlocks: () =>
      request<{
        blocks: {
          user: {
            id: string;
            platformId: string;
            username: string;
            displayName: string | null;
            avatarUrl: string | null;
          };
          createdAt: string;
        }[];
      }>('/api/me/blocks'),
    blockUser: (userId: string) =>
      request<{ ok: true }>('/api/me/blocks', {
        method: 'POST',
        body: JSON.stringify({ userId }),
      }),
    unblockUser: (userId: string) =>
      request<void>(`/api/me/blocks/${userId}`, { method: 'DELETE' }),
    /** Ids of users who have blocked the current user (one-way DM block). */
    listBlockedBy: () => request<{ userIds: string[] }>('/api/me/blocked-by'),

    // ---------- friends ----------
    listFriends: () => request<FriendsSnapshot>('/api/me/friends'),
    /** Send a friend request (or accept a reciprocal pending one). */
    sendFriendRequest: (userId: string) =>
      request<{ ok: true; status: FriendState }>('/api/me/friends', {
        method: 'POST',
        body: JSON.stringify({ userId }),
      }),
    acceptFriendRequest: (userId: string) =>
      request<{ ok: true }>(`/api/me/friends/${userId}/accept`, { method: 'POST' }),
    /** Decline / cancel / unfriend — all map to removing the relationship. */
    removeFriend: (userId: string) =>
      request<void>(`/api/me/friends/${userId}`, { method: 'DELETE' }),

    // ---------- global message search ----------
    /** Cross-channel search, ACL-filtered server-side to channels the user can
     *  actually read (guild visibility + DM participation). */
    searchGlobal: (q: string) =>
      request<{ messages: GlobalSearchHit[] }>(`/api/me/search?q=${encodeURIComponent(q)}`),

    // ---------- DM folders (Telegram-style) ----------
    listDmFolders: () => request<DmFoldersSnapshot>('/api/me/dm-folders'),
    createDmFolder: (name: string) =>
      request<DmFolder>('/api/me/dm-folders', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    updateDmFolder: (id: string, input: { name?: string; position?: number }) =>
      request<DmFolder>(`/api/me/dm-folders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    deleteDmFolder: (id: string) =>
      request<{ ok: true }>(`/api/me/dm-folders/${id}`, { method: 'DELETE' }),
    addDmFolderEntry: (folderId: string, channelId: string) =>
      request<{ ok: true }>(`/api/me/dm-folders/${folderId}/entries/${channelId}`, {
        method: 'PUT',
      }),
    removeDmFolderEntry: (folderId: string, channelId: string) =>
      request<{ ok: true }>(`/api/me/dm-folders/${folderId}/entries/${channelId}`, {
        method: 'DELETE',
      }),

    // ---------- server folders (Discord-style rail grouping) ----------
    listGuildFolders: () => request<GuildFoldersSnapshot>('/api/me/guild-folders'),
    /** Create a folder from servers — the drag-onto-another "merge" gesture. */
    createGuildFolder: (input: { guildIds: string[]; name?: string; color?: string }) =>
      request<GuildFolder>('/api/me/guild-folders', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    updateGuildFolder: (
      id: string,
      input: { name?: string | null; color?: string | null; position?: number },
    ) =>
      request<GuildFolder>(`/api/me/guild-folders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    deleteGuildFolder: (id: string) =>
      request<{ ok: true }>(`/api/me/guild-folders/${id}`, { method: 'DELETE' }),
    addGuildFolderEntry: (folderId: string, guildId: string) =>
      request<{ ok: true }>(`/api/me/guild-folders/${folderId}/entries/${guildId}`, {
        method: 'PUT',
      }),
    removeGuildFolderEntry: (folderId: string, guildId: string) =>
      request<{ ok: true; dissolved: boolean }>(
        `/api/me/guild-folders/${folderId}/entries/${guildId}`,
        { method: 'DELETE' },
      ),

    // ---------- sessions / devices ----------
    listSessions: () =>
      request<{
        sessions: {
          id: string;
          current: boolean;
          createdAt: string;
          lastSeenAt: string;
          uaHint: string | null;
        }[];
      }>('/api/me/sessions'),
    revokeSession: (id: string) =>
      request<void>(`/api/me/sessions/${id}`, { method: 'DELETE' }),
    revokeOtherSessions: () =>
      request<{ ok: true; revoked: number }>('/api/me/sessions/revoke-others', {
        method: 'POST',
      }),
  };
}
